import React from 'react';
import { PageSection } from '../../types/page';
import { ContactForm } from '../common/ContactForm';
import { ContactInfo } from '../ui/ContactInfo';
import { useTenant } from '../../hooks/useTenant';
import { MapPin, Phone, Mail, Clock, MessageCircle } from 'lucide-react';

interface ContactFormSectionContent {
  layout?: 'form-only' | 'form-with-info' | 'side-by-side';
  showContactInfo?: boolean;
  showMap?: boolean;
  backgroundImage?: string;
  formTitle?: string;
  formSubtitle?: string;
  infoTitle?: string;
  contactInfo?: {
    address?: string;
    phone?: string;
    email?: string;
    hours?: string;
    whatsapp?: string;
  };
  mapUrl?: string;
}

interface ContactFormSectionProps {
  section: PageSection;
  className?: string;
}

export const ContactFormSection: React.FC<ContactFormSectionProps> = ({ 
  section, 
  className = '' 
}) => {
  const content = section.content as ContactFormSectionContent;
  const { config } = useTenant();
  
  const backgroundStyle = content.backgroundImage 
    ? { backgroundImage: `url(${content.backgroundImage})` }
    : {};

  const layout = content.layout || 'form-with-info';

  // Prepare contact info data
  const contactInfoItems = [];
  
  if (content.contactInfo?.address || config?.contact?.address) {
    contactInfoItems.push({
      icon: MapPin,
      title: 'Dirección',
      content: content.contactInfo?.address || config?.contact?.address || ''
    });
  }
  
  if (content.contactInfo?.phone || config?.contact?.phone) {
    contactInfoItems.push({
      icon: Phone,
      title: 'Teléfono',
      content: content.contactInfo?.phone || config?.contact?.phone || ''
    });
  }
  
  if (content.contactInfo?.email || config?.contact?.email) {
    contactInfoItems.push({
      icon: Mail,
      title: 'Email',
      content: content.contactInfo?.email || config?.contact?.email || ''
    });
  }
  
  if (content.contactInfo?.hours) {
    contactInfoItems.push({
      icon: Clock,
      title: 'Horarios',
      content: content.contactInfo.hours
    });
  }
  
  if (content.contactInfo?.whatsapp || config?.contact?.whatsapp) {
    contactInfoItems.push({
      icon: MessageCircle,
      title: 'WhatsApp',
      content: content.contactInfo?.whatsapp || config?.contact?.whatsapp || ''
    });
  }

  const renderForm = () => (
    <div className={`${layout === 'side-by-side' ? '' : 'max-w-2xl mx-auto'}`}>
      <div className={`p-8 rounded-xl ${content.backgroundImage ? 'bg-white/95 backdrop-blur-sm' : 'bg-white shadow-lg border'}`}>
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {content.formTitle || 'Contáctanos'}
          </h3>
          {content.formSubtitle && (
            <p className="text-gray-600">
              {content.formSubtitle}
            </p>
          )}
        </div>
        
        <ContactForm 
          source="contact"
          className="space-y-6"
        />
      </div>
    </div>
  );

  const renderContactInfo = () => (
    <div className={`${layout === 'side-by-side' ? '' : 'max-w-2xl mx-auto mt-12'}`}>
      <div className={`p-8 rounded-xl ${content.backgroundImage ? 'bg-white/95 backdrop-blur-sm' : 'bg-white shadow-lg border'}`}>
        <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
          {content.infoTitle || 'Información de Contacto'}
        </h3>
        
        <ContactInfo items={contactInfoItems} />
        
        {/* Map */}
        {content.showMap && content.mapUrl && (
          <div className="mt-8">
            <iframe
              src={content.mapUrl}
              width="100%"
              height="300"
              className="rounded-lg border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mapa de ubicación de la oficina"
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <section 
      className={`relative py-20 bg-cover bg-center bg-no-repeat ${content.backgroundImage ? '' : 'bg-gray-50'} ${className}`}
      style={backgroundStyle}
    >
      {/* Overlay */}
      {content.backgroundImage && (
        <div className="absolute inset-0 bg-black/40" />
      )}
      
      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
            content.backgroundImage ? 'text-white' : 'text-gray-900'
          }`}>
            {section.title || 'Contacto'}
          </h2>
          {section.subtitle && (
            <p className={`text-xl max-w-2xl mx-auto ${
              content.backgroundImage ? 'text-gray-200' : 'text-gray-600'
            }`}>
              {section.subtitle}
            </p>
          )}
        </div>

        {/* Content based on layout */}
        {layout === 'form-only' && renderForm()}
        
        {layout === 'form-with-info' && (
          <div className="space-y-12">
            {renderForm()}
            {content.showContactInfo !== false && contactInfoItems.length > 0 && renderContactInfo()}
          </div>
        )}
        
        {layout === 'side-by-side' && (
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {renderForm()}
            {content.showContactInfo !== false && contactInfoItems.length > 0 && renderContactInfo()}
          </div>
        )}
      </div>
    </section>
  );
};